import React, { useEffect, useRef, useState } from 'react';
import Container from '@material-ui/core/Container';
import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';
import './Contact.css';

const emptyForm = { subject: '', email: '', targetRecipients: [], descriptions: '' };

export default function Contact() {
    const [form, setForm] = useState(emptyForm);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const submitting = useRef(false);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        async function load() {
            setLoading(true);
            setLoadError(false);
            try {
                const { apiUrl } = await initializeStrapiBackend();
                const response = await fetch(`${apiUrl}/contact-forms/recipients`, {
                    credentials: 'omit', signal: controller.signal
                });
                if (!response.ok) throw new Error('Recipients unavailable');
                const result = await response.json();
                if (!Array.isArray(result.data) || result.data.some(item =>
                    !Number.isInteger(item.id) || item.id <= 0 || typeof item.directorateName !== 'string'
                )) throw new Error('Invalid recipients');
                if (active) setRecipients(result.data);
            } catch {
                if (active) setLoadError(true);
            } finally {
                if (active) setLoading(false);
            }
        }
        load();
        return () => { active = false; controller.abort(); };
    }, [attempt]);

    function change(event) {
        setSuccess(false);
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    async function submit(event) {
        event.preventDefault();
        if (submitting.current) return;
        setSuccess(false);
        setError('');
        const data = {
            subject: form.subject.trim(), email: form.email.trim(),
            targetRecipients: form.targetRecipients, descriptions: form.descriptions.trim()
        };
        if (!data.subject || data.subject.length > 200 || !data.email || data.email.length > 254 ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) ||
            !data.descriptions || data.descriptions.length > 5000) {
            setError('Complete all fields with a valid email address and stay within the character limits.');
            return;
        }
        if (loading || loadError || data.targetRecipients.length < 1 || data.targetRecipients.length > 10 ||
            data.targetRecipients.some(id => !recipients.some(recipient => recipient.id === id))) {
            setError('Select between 1 and 10 directorates.');
            return;
        }
        submitting.current = true;
        setBusy(true);
        try {
            const { apiUrl } = await initializeStrapiBackend();
            const response = await fetch(`${apiUrl}/contact-forms`, {
                method: 'POST', credentials: 'omit',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data })
            });
            if (response.status === 429) {
                setError('Too many submissions. Please wait one minute before trying again.');
            } else if (response.status === 400) {
                setError('Your submission could not be accepted. Check your details and selected directorates, then try again.');
            } else if (response.status !== 201) {
                setError('We could not confirm receipt of your message. Please try again later.');
            } else {
                setForm(emptyForm);
                setSuccess(true);
            }
        } catch {
            setError('We could not confirm receipt of your message. Check your connection before trying again.');
        } finally {
            submitting.current = false;
            setBusy(false);
        }
    }

    return (
        <Container maxWidth="md">
            <section className="contact-page" aria-labelledby="contact-title">
                <h1 id="contact-title">Contact the Ministry</h1>
                <p>Send your enquiry to the relevant directorates. All four fields are required.</p>
                <form onSubmit={submit} className="contact-form" aria-busy={busy}>
                    <fieldset disabled={busy} className="contact-fields">
                        <label htmlFor="contact-subject">Subject</label>
                        <input id="contact-subject" name="subject" value={form.subject} onChange={change} required maxLength={200} />
                        <label htmlFor="contact-email">Email</label>
                        <input id="contact-email" name="email" type="email" autoComplete="email" value={form.email} onChange={change} required maxLength={254} />
                        <fieldset className="contact-recipients" aria-describedby="contact-recipient-help">
                            <legend>Target recipients (Directorates)</legend>
                            <p id="contact-recipient-help">Select 1–10 directorates. {form.targetRecipients.length} selected.</p>
                            {loading && <p role="status">Loading directorates…</p>}
                            {loadError && <div role="alert">Directorates could not be loaded. <button type="button" onClick={() => setAttempt(attempt + 1)}>Retry</button></div>}
                            {!loading && !loadError && recipients.length === 0 && <p role="status">No directorates are currently available. Please try again later.</p>}
                            {!loading && !loadError && recipients.map(recipient => (
                                <label className="contact-recipient" key={recipient.id}>
                                    <input type="checkbox" checked={form.targetRecipients.includes(recipient.id)}
                                        disabled={form.targetRecipients.length >= 10 && !form.targetRecipients.includes(recipient.id)}
                                        onChange={event => {
                                            setSuccess(false);
                                            setForm({ ...form, targetRecipients: event.target.checked
                                                ? [...form.targetRecipients, recipient.id]
                                                : form.targetRecipients.filter(id => id !== recipient.id) });
                                        }} />
                                    {recipient.directorateName}
                                </label>
                            ))}
                        </fieldset>
                        <label htmlFor="contact-descriptions">Descriptions</label>
                        <textarea id="contact-descriptions" name="descriptions" rows={7} value={form.descriptions} onChange={change} required maxLength={5000} aria-describedby="contact-description-help" />
                        <p id="contact-description-help" className="contact-help">{form.descriptions.length}/5000 characters</p>
                        <button className="contact-submit" type="submit" disabled={busy || loading || loadError || !recipients.length}>{busy ? 'Submitting…' : 'Send message'}</button>
                    </fieldset>
                    {error && <p role="alert" className="contact-error">{error}</p>}
                    {success && <p role="status" className="contact-success">Thank you. Your message has been received.</p>}
                </form>
            </section>
        </Container>
    );
}
